import {useCallback, useMemo, useState} from 'react'
import {PinIcon, TriangleDownIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Button, Tooltip} from '@primer/react'
import {useClickAnalytics} from '@github-ui/use-analytics'
import {useNavigate} from '@github-ui/use-navigate'
import {useUserPinMutation} from './hooks/use-user-pin-mutation'
import {useOrgPinMutation} from './hooks/use-org-pin-mutation'
import type {OrgPinData, OrgPinFormState, OrgPinOptionName, PinButtonProps} from './types'

function buildInitialOrgState(orgPinData: OrgPinData): OrgPinFormState {
  return {
    'public-profile': orgPinData.isPinnedPublicly,
    'internal-profile': orgPinData.isPinnedInternally,
    'user-profile': orgPinData.isPinnedToViewerProfile,
  }
}

export function hasOrgOptions(orgPinData?: OrgPinData | null): orgPinData is OrgPinData {
  return Boolean(
    orgPinData &&
    (orgPinData.canPinPublicly ||
      orgPinData.canPinInternally ||
      orgPinData.canPinToViewerProfile ||
      orgPinData.isPinnedPublicly ||
      orgPinData.isPinnedInternally ||
      orgPinData.isPinnedToViewerProfile),
  )
}

function getOrgPinTooltipText(orgPinData: OrgPinData): string {
  const canManageOrgPins =
    orgPinData.canPinPublicly ||
    orgPinData.canPinInternally ||
    orgPinData.isPinnedPublicly ||
    orgPinData.isPinnedInternally
  const canManageViewerPin = orgPinData.canPinToViewerProfile || orgPinData.isPinnedToViewerProfile

  if (canManageOrgPins && canManageViewerPin) {
    return 'Pin or unpin this repository on organization profiles and your profile'
  }

  if (canManageOrgPins) {
    return 'Pin or unpin this repository on organization profiles'
  }

  return 'Pin or unpin this repository on your profile'
}

function OrgPinOption({
  checked,
  description,
  disabled,
  label,
  onSelect,
}: {
  checked: boolean
  description: string
  disabled: boolean
  label: string
  onSelect: () => void
}) {
  return (
    <ActionList.Item
      selected={checked}
      disabled={disabled}
      inactiveText={disabled ? 'Limit reached' : undefined}
      onSelect={event => {
        event.preventDefault()
        onSelect()
      }}
    >
      {label}
      <ActionList.Description variant="block">{description}</ActionList.Description>
    </ActionList.Item>
  )
}

function OrgPinMenu({
  orgPinData,
  repoUrl,
  onSuccess,
  onError,
  size = 'small',
}: {
  orgPinData: OrgPinData
  repoUrl: string
  onSuccess?: () => void
  onError?: (error: Error) => void
  size?: 'small' | 'medium'
}) {
  const [formState, setFormState] = useState<OrgPinFormState>(() => buildInitialOrgState(orgPinData))
  const [isOpen, setIsOpen] = useState(false)
  const tooltipText = useMemo(() => getOrgPinTooltipText(orgPinData), [orgPinData])
  const {sendClickAnalyticsEvent} = useClickAnalytics()

  const orgPinMutation = useOrgPinMutation({repoUrl})
  const {mutate: submitOrgPin} = orgPinMutation

  const toggleOption = useCallback(
    (optionName: OrgPinOptionName) => {
      if (orgPinMutation.isPending) return

      const previousState = formState
      const nextState: OrgPinFormState = {...previousState, [optionName]: !previousState[optionName]}
      setFormState(nextState)

      sendClickAnalyticsEvent({
        category: 'REPOSITORY_OVERVIEW',
        action: `click:org_pin_${optionName}`,
        label: `location:header;ref_cta:org_pin_menu;value:${nextState[optionName] ? 'on' : 'off'}`,
      })

      submitOrgPin(nextState, {
        onSuccess,
        onError: (error: Error) => {
          setFormState(previousState)
          onError?.(error)
        },
      })
    },
    [formState, onError, onSuccess, orgPinMutation.isPending, sendClickAnalyticsEvent, submitOrgPin],
  )

  return (
    <ActionMenu open={isOpen} onOpenChange={setIsOpen}>
      <Tooltip text={tooltipText} type="description" direction="s">
        <ActionMenu.Button
          size={size}
          leadingVisual={PinIcon}
          trailingAction={TriangleDownIcon}
          data-testid="pin-button-summary"
          /* eslint-disable-next-line @github-ui/dotcom-primer/no-title-case -- baselined during linter rollout */
        >
          Edit Pins
        </ActionMenu.Button>
      </Tooltip>
      <ActionMenu.Overlay align="end" width="medium">
        <ActionList selectionVariant="multiple" aria-label="Pin repository locations">
          {orgPinData.canPinPublicly || orgPinData.isPinnedPublicly ? (
            <OrgPinOption
              checked={formState['public-profile']}
              description="Visible to anyone"
              disabled={!orgPinData.isPinnedPublicly && orgPinData.publicPinsRemaining === 0}
              label="Public pins in this organization"
              onSelect={() => toggleOption('public-profile')}
            />
          ) : null}
          {orgPinData.canPinInternally || orgPinData.isPinnedInternally ? (
            <OrgPinOption
              checked={formState['internal-profile']}
              description="Visible to members only"
              disabled={!orgPinData.isPinnedInternally && orgPinData.internalPinsRemaining === 0}
              label="Private pins in this organization"
              onSelect={() => toggleOption('internal-profile')}
            />
          ) : null}
          {orgPinData.canPinToViewerProfile || orgPinData.isPinnedToViewerProfile ? (
            <OrgPinOption
              checked={formState['user-profile']}
              description="Pin this to your personal profile, visible to everyone"
              disabled={!orgPinData.isPinnedToViewerProfile && orgPinData.viewerPinsRemaining === 0}
              label="Profile"
              onSelect={() => toggleOption('user-profile')}
            />
          ) : null}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

export function shouldShowPinButton(props: PinButtonProps) {
  const {isOrgOwned, canPin, isPrivate, orgPinData, isLoggedIn} = props
  if (!isLoggedIn) {
    return false
  }

  if (isOrgOwned && !hasOrgOptions(orgPinData)) {
    return false
  }

  if (canPin && !isPrivate) {
    return true
  }

  return false
}

export function PinButton({
  repoNameWithOwner,
  repoUrl,
  isPrivate,
  isOrgOwned,
  canPin,
  isPinned,
  pinItemsRemaining,
  orgPinData,
  onSuccess,
  onError,
  size = 'small',
}: PinButtonProps) {
  const userPinPath = `${repoUrl}/profile_pin`
  const [isPinnedState, setIsPinnedState] = useState(isPinned)
  const [remainingPinSlots, setRemainingPinSlots] = useState(pinItemsRemaining)
  const {sendClickAnalyticsEvent} = useClickAnalytics()
  const navigate = useNavigate()

  const userPinMutation = useUserPinMutation({pinPath: userPinPath})
  const {mutate: submitUserPin} = userPinMutation

  const isDisabled = !isPinnedState && remainingPinSlots === 0
  const ownerProfile = `/${repoNameWithOwner.split('/')[0]}`

  const handleUserPinClick = useCallback(() => {
    if (userPinMutation.isPending || isDisabled) return

    const previousIsPinned = isPinnedState
    const previousRemaining = remainingPinSlots

    setIsPinnedState(!previousIsPinned)
    setRemainingPinSlots(previousIsPinned ? previousRemaining + 1 : previousRemaining - 1)

    sendClickAnalyticsEvent({
      category: 'REPOSITORY_OVERVIEW',
      action: previousIsPinned ? 'click:unpin' : 'click:pin',
      label: 'location:header;ref_cta:pin_button',
    })

    submitUserPin(
      {isPinned: previousIsPinned},
      {
        onSuccess: () => {
          onSuccess?.()
          navigate(ownerProfile)
        },
        onError: (error: Error) => {
          setIsPinnedState(previousIsPinned)
          setRemainingPinSlots(previousRemaining)
          onError?.(error)
        },
      },
    )
  }, [
    isDisabled,
    isPinnedState,
    navigate,
    onError,
    onSuccess,
    ownerProfile,
    remainingPinSlots,
    sendClickAnalyticsEvent,
    submitUserPin,
    userPinMutation.isPending,
  ])

  if (isOrgOwned) {
    if (!hasOrgOptions(orgPinData)) {
      return null
    }

    return <OrgPinMenu orgPinData={orgPinData} repoUrl={repoUrl} onSuccess={onSuccess} onError={onError} size={size} />
  }

  const shouldRenderUserButton = canPin && (isPinnedState || !isPrivate)
  if (!shouldRenderUserButton) {
    return null
  }

  const userTooltip = !isPinnedState
    ? isDisabled
      ? 'No pin slots remaining in your profile'
      : `Pin ${repoNameWithOwner} to your profile`
    : `Unpin ${repoNameWithOwner} from your profile`

  return (
    <Tooltip text={userTooltip} type="description" direction="s">
      <Button
        type="button"
        size={size}
        inactive={isDisabled}
        aria-disabled={isDisabled}
        leadingVisual={PinIcon}
        aria-label={`${isPinnedState ? 'Unpin' : 'Pin'} ${repoNameWithOwner}`}
        data-testid="pin-button"
        onClick={handleUserPinClick}
      >
        {isPinnedState ? 'Unpin' : 'Pin'}
      </Button>
    </Tooltip>
  )
}
